import { Ring, TimeWindow, percentile } from "./window.js";

export interface TimelineOptions {
  /** Server step length in ms (1000 / tickHz). */
  stepMs: number;
  /** Added to one snapshot interval + p95 delivery jitter (P1-S01: 10). */
  marginMs?: number;
  /** Lowest buffer. Default stepMs. */
  minBufferMs?: number;
  /**
   * Highest buffer target. Default 600 ms, high on purpose: on lossy mobile links a lost WebSocket
   * (TCP) segment stalls delivery for a retransmit timeout (200-400 ms). Absorbing one stall into the
   * buffer, and holding it, beats freezing on every one. Rarer, longer stalls (a segment lost twice)
   * freeze briefly and are then caught up at `catchUpPerSec`.
   */
  maxBufferMs?: number;
  /** Buffer before there is enough delivery history (P1-S01's fixed buffer). Default 100. */
  initialBufferMs?: number;
  /** Delivery history for the offset (window minimum) and jitter (p95). Default 10 s. */
  windowMs?: number;
  /** How fast the buffer may grow and shrink, in ms per second, so 60 and 144 FPS behave alike. */
  growPerSec?: number;
  shrinkPerSec?: number;
  /** Shrink rate while above maxBufferMs after a long stall (playback runs 1.25× to catch up). */
  catchUpPerSec?: number;
  /**
   * After a stall, keep enough buffer to absorb another like it until no stall-sized delivery has been
   * seen for this long (bursts recur). Default 30 s. 0 = latency first: track p95 jitter only, and
   * accept underruns on retransmit stalls (measurements in the README).
   */
  holdMs?: number;
  /** On underrun, extrapolate this far past the newest snapshot, then freeze (P1-S01: 100). */
  maxExtrapolateMs?: number;
}

export interface TimelineSample<S> {
  /** Interpolate between these two snapshots at `alpha`. */
  from: S;
  to: S;
  /** 0-1 between `from` and `to`; above 1 while extrapolating during an underrun. */
  alpha: number;
  /** Server tick being shown (fractional). */
  renderTick: number;
  /** True while the render clock is past the newest snapshot. */
  underrun: boolean;
}

export interface TimelineStats {
  /** Current buffer: how far rendering trails the fastest observed delivery. */
  bufferMs: number;
  /** Jitter-based target: one snapshot interval + p95 delivery jitter + margin, clamped. */
  targetMs: number;
  /** Extra buffer kept after a stall (0 when none). */
  stallFloorMs: number;
  /** Times the render clock ran past the newest snapshot (episodes, extrapolated or not). */
  underruns: number;
  /** Underruns that outlasted extrapolation, so motion visibly stopped. */
  freezes: number;
  snapshots: number;
  /** Duplicates and out-of-order snapshots that were ignored. */
  stale: number;
  /** Time between snapshot arrivals over the window. */
  gapMs: { p50: number; p95: number; max: number };
}

interface Snap<S> {
  tick: number;
  state: S;
}

/**
 * Client-side interpolation on server time with an adaptive jitter buffer (P1-S01 → P2-SIM-03).
 *
 * - Snapshot n belongs at n · stepMs on the server's clock. The smallest observed
 *   (arrival − n · stepMs) over the window maps local time onto that axis (one-way delay + clock
 *   offset). Rendering runs `delay` behind local time, so arrival jitter never changes playback speed.
 * - Buffer target = one snapshot interval + p95 of delivery delay above the fastest + margin.
 *   The buffer moves towards it at a bounded rate per second (not per frame).
 * - Underrun: extrapolate up to maxExtrapolateMs, then freeze the render clock: the stall goes into
 *   the buffer, so playback never jumps forward when the late data arrives. Afterwards the buffer
 *   keeps room for a stall that size (the stall floor) until stalls stop for `holdMs`.
 * - Underruns are also accounted at arrival time, not only at frames, so the frame rate doesn't
 *   change the outcome.
 *
 * Pure: the host passes its clock (performance.now) to `push` and `sample`; no timers.
 */
export class SnapshotTimeline<S> {
  readonly stepMs: number;
  readonly #o: Required<TimelineOptions>;
  #snaps: Snap<S>[] = [];
  readonly #offsets: TimeWindow;
  readonly #gaps: TimeWindow;
  readonly #tickDeltas = new Ring(16);
  #lastArrival = Number.NaN;
  /** Render delay: local time minus the server time being shown, in ms. */
  #delay = Number.NaN;
  #lastNow = Number.NaN;
  #targetBuffer: number;
  #intervalMs: number;
  #targetAt = Number.NEGATIVE_INFINITY;
  #holdUntil = Number.NEGATIVE_INFINITY;
  #stallFloor = 0;
  #inUnderrun = false;
  #inFreeze = false;
  /** How far past the newest snapshot this underrun rendered (the part not absorbed), ms. */
  #overshootMs = 0;
  #underruns = 0;
  #freezes = 0;
  #snapshots = 0;
  #stale = 0;

  constructor(options: TimelineOptions) {
    this.stepMs = options.stepMs;
    this.#o = {
      stepMs: options.stepMs,
      marginMs: options.marginMs ?? 10,
      minBufferMs: options.minBufferMs ?? options.stepMs,
      maxBufferMs: options.maxBufferMs ?? 600,
      initialBufferMs: options.initialBufferMs ?? 100,
      windowMs: options.windowMs ?? 10_000,
      growPerSec: options.growPerSec ?? 120,
      shrinkPerSec: options.shrinkPerSec ?? 30,
      catchUpPerSec: options.catchUpPerSec ?? 250,
      holdMs: options.holdMs ?? 30_000,
      maxExtrapolateMs: options.maxExtrapolateMs ?? 100,
    };
    this.#offsets = new TimeWindow(this.#o.windowMs);
    this.#gaps = new TimeWindow(this.#o.windowMs);
    this.#targetBuffer = this.#o.initialBufferMs;
    this.#intervalMs = options.stepMs;
  }

  /** A snapshot of server tick `tick` arrived at local time `now`. Duplicates and older ticks are ignored. */
  push(tick: number, state: S, now: number): void {
    const latest = this.#snaps[this.#snaps.length - 1];
    if (latest && tick <= latest.tick) {
      this.#stale++;
      return;
    }
    if (latest) {
      this.#tickDeltas.push(tick - latest.tick);
      // Was the render clock already past `latest` when this arrived? Account for it exactly now.
      this.#overrun(now, latest);
    }
    this.#snaps.push({ tick, state });
    this.#snapshots++;
    const offset = now - tick * this.stepMs;
    this.#offsets.push(now, offset);
    if (!Number.isNaN(this.#lastArrival)) this.#gaps.push(now, now - this.#lastArrival);
    this.#lastArrival = now;
    if (Number.isNaN(this.#delay)) this.#delay = this.#offsets.min + this.#o.initialBufferMs;
    // A stall-sized delivery keeps the stall floor alive, even when the floor absorbed it.
    if (this.#stallFloor > 0 && offset - this.#offsets.min + this.#intervalMs > this.#targetBuffer)
      this.#holdUntil = Math.max(this.#holdUntil, now + this.#o.holdMs);
  }

  /**
   * Underrun bookkeeping at local time `now` while `latest` is the newest snapshot: count the
   * episode, freeze the render clock past the extrapolation limit, and note the overshoot.
   * Returns the (possibly frozen) render tick.
   */
  #overrun(now: number, latest: Snap<S>): number {
    const o = this.#o;
    let renderTick = (now - this.#delay) / this.stepMs;
    if (!(renderTick > latest.tick)) return renderTick;
    if (!this.#inUnderrun) this.#underruns++;
    this.#inUnderrun = true;
    this.#holdUntil = Math.max(this.#holdUntil, now + o.holdMs);
    const limit = latest.tick + o.maxExtrapolateMs / this.stepMs;
    if (renderTick > limit) {
      if (!this.#inFreeze) this.#freezes++;
      this.#inFreeze = true;
      // Past a few seconds the connection is effectively gone: stop absorbing (the host resets on
      // reconnect). Above maxBufferMs the buffer is caught up at catchUpPerSec afterwards.
      const maxDelay = this.#offsets.min + 5 * o.maxBufferMs;
      this.#delay = Math.max(this.#delay, Math.min(maxDelay, now - limit * this.stepMs));
      renderTick = (now - this.#delay) / this.stepMs;
    }
    this.#overshootMs = Math.max(
      this.#overshootMs,
      (Math.min(renderTick, limit) - latest.tick) * this.stepMs,
    );
    return renderTick;
  }

  /**
   * What to render at local time `now`: call once per frame. Also adapts the buffer by elapsed time,
   * not per call. Null until the first snapshot.
   */
  sample(now: number): TimelineSample<S> | null {
    const latest = this.#snaps[this.#snaps.length - 1];
    if (!latest) return null;
    const o = this.#o;
    const dt = Number.isNaN(this.#lastNow) ? 0 : Math.max(0, now - this.#lastNow);
    this.#lastNow = now;

    if (now - this.#targetAt >= 1000) this.#retarget(now);
    if (now >= this.#holdUntil) this.#stallFloor = 0;
    const minOffset = this.#offsets.min;
    const buffer = this.#delay - minOffset;
    const target = Math.min(o.maxBufferMs, Math.max(this.#targetBuffer, this.#stallFloor));
    if (buffer > o.maxBufferMs && !this.#inUnderrun) {
      // Over the ceiling after a long stall: catch up smoothly instead of jumping.
      this.#delay = Math.max(minOffset + target, this.#delay - (o.catchUpPerSec * dt) / 1000);
    } else if (target > buffer) {
      if (target - buffer > o.marginMs) this.#holdUntil = Math.max(this.#holdUntil, now + o.holdMs);
      this.#delay = Math.min(minOffset + target, this.#delay + (o.growPerSec * dt) / 1000);
    } else if (now >= this.#holdUntil) {
      this.#delay = Math.max(minOffset + target, this.#delay - (o.shrinkPerSec * dt) / 1000);
    }

    const renderTick = this.#overrun(now, latest);
    if (renderTick > latest.tick) {
      const prev = this.#snaps[this.#snaps.length - 2];
      if (!prev)
        return { from: latest.state, to: latest.state, alpha: 1, renderTick, underrun: true };
      const shown = Math.min(renderTick, latest.tick + o.maxExtrapolateMs / this.stepMs);
      return {
        from: prev.state,
        to: latest.state,
        alpha: (shown - prev.tick) / (latest.tick - prev.tick),
        renderTick,
        underrun: true,
      };
    }
    if (this.#inUnderrun) {
      // The stall is over. Keep enough buffer to absorb one like it (reached at growPerSec).
      const floor = this.#delay - minOffset + this.#overshootMs + o.marginMs;
      this.#stallFloor = Math.max(this.#stallFloor, floor);
      this.#overshootMs = 0;
      this.#inUnderrun = false;
      this.#inFreeze = false;
    }

    // Newest snapshot at or before renderTick, and the one after it.
    let i = this.#snaps.length - 1;
    while (i > 0 && (this.#snaps[i]?.tick ?? 0) > renderTick) i--;
    const a = this.#snaps[i] as Snap<S>;
    const b = this.#snaps[i + 1] ?? a;
    if (i > 1) this.#snaps = this.#snaps.slice(i - 1); // keep one older snapshot for extrapolation
    const alpha = b === a ? 0 : Math.max(0, (renderTick - a.tick) / (b.tick - a.tick));
    return { from: a.state, to: b.state, alpha, renderTick, underrun: false };
  }

  #retarget(now: number): void {
    this.#targetAt = now;
    const o = this.#o;
    if (this.#offsets.size < 20) {
      this.#targetBuffer = o.initialBufferMs;
      return;
    }
    this.#intervalMs = percentile(this.#tickDeltas.values, 50) * this.stepMs || this.stepMs;
    const jitter = this.#offsets.excess(95);
    this.#targetBuffer = Math.min(
      o.maxBufferMs,
      Math.max(o.minBufferMs, this.#intervalMs + jitter + o.marginMs),
    );
  }

  /** Forget everything: call on every (re)connect, since a restarted server may shift its tick clock. */
  reset(): void {
    this.#snaps = [];
    this.#offsets.clear();
    this.#gaps.clear();
    this.#tickDeltas.clear();
    this.#lastArrival = Number.NaN;
    this.#delay = Number.NaN;
    this.#lastNow = Number.NaN;
    this.#targetBuffer = this.#o.initialBufferMs;
    this.#intervalMs = this.stepMs;
    this.#targetAt = Number.NEGATIVE_INFINITY;
    this.#holdUntil = Number.NEGATIVE_INFINITY;
    this.#stallFloor = 0;
    this.#inUnderrun = false;
    this.#inFreeze = false;
    this.#overshootMs = 0;
  }

  /** For telemetry (P2-AN-04 perf_sample). Counters are cumulative across resets. */
  get stats(): TimelineStats {
    const gaps = this.#gaps.values();
    return {
      bufferMs: Number.isNaN(this.#delay) ? Number.NaN : this.#delay - this.#offsets.min,
      targetMs: this.#targetBuffer,
      stallFloorMs: this.#stallFloor,
      underruns: this.#underruns,
      freezes: this.#freezes,
      snapshots: this.#snapshots,
      stale: this.#stale,
      gapMs: {
        p50: percentile(gaps, 50),
        p95: percentile(gaps, 95),
        max: gaps.length > 0 ? Math.max(...gaps) : Number.NaN,
      },
    };
  }
}
